import dayjs from "dayjs";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import {
  ChartFooter,
  ChartHeader,
  DepositAdjuster,
  ProjectedChart,
  ProjectionCalculationModal,
} from "@components/ProjectedChart";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Caption } from "@components/Typography";
import { DEFAULT_RECURRING_AMOUNT, DEFAULT_TARGET_GOAL_YEAR } from "@constants";
import {
  GoalsContextProvider,
  ProjectedChartProvider,
  ProjectedChartProviderProps,
} from "@contexts";
import { Flow, SipFrequency, SipStatus } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useGoalFlow, useGoalsContext, useProjectedChart } from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  calculateTotalInviteAmountInCents,
  convertCentsToDollar,
  convertToMultiplier,
  getMinDepositAmount,
  getSipStartInfoKey,
} from "@utils";
import { useGoalDepositScreenFlow } from "./flow";
import { SharedContribution } from "./SharedContribution";
import { styles } from "./styles";
import { TargetYearAdjuster } from "./TargetYearAdjuster";

export const GoalDepositRoute = () => {
  const {
    goalProps: { beneficiaryId, goalId },
  } = useGoalFlow();

  return (
    <GoalsContextProvider beneficiaryId={beneficiaryId} goalId={goalId}>
      <GoalDepositRouteWrapper />
    </GoalsContextProvider>
  );
};

export const GoalDepositRouteWrapper = () => {
  const {
    goalProps: { flow, pendingInvite, accountType: accountTypeFromGoalFlow },
  } = useGoalFlow();

  const isAcceptGoalFlow = flow === Flow.AcceptSharedGoal;

  const { goalLengthInYears, multiplier: multiplierFromGoal, goal } = useGoalsContext();

  const accountType = goal?.accounts?.[0]?.type ?? accountTypeFromGoalFlow ?? undefined;

  const multiplier = (() => {
    if (isAcceptGoalFlow && pendingInvite?.projectedRateOfReturn) {
      return convertToMultiplier(pendingInvite.projectedRateOfReturn);
    }

    return multiplierFromGoal;
  })();

  const projectedAge = (() => {
    if (isAcceptGoalFlow && pendingInvite?.targetYear && pendingInvite.startDate) {
      const startYear = dayjs(pendingInvite.startDate).year();

      return pendingInvite.targetYear - startYear;
    }

    return goalLengthInYears > 0 ? goalLengthInYears : DEFAULT_TARGET_GOAL_YEAR;
  })();

  const contributeAfter18 = (() => {
    if (isAcceptGoalFlow && pendingInvite?.contributionsAfter18 != null) {
      return pendingInvite.contributionsAfter18;
    }

    return goal?.contributionsAfter18 ?? true;
  })();

  const recurringDepositInCents = (() => {
    if (isAcceptGoalFlow && pendingInvite?.recurringDepositInCents) {
      return calculateTotalInviteAmountInCents(pendingInvite);
    }

    return goal?.sips?.[0]?.recurringDepositInCents;
  })();

  const frequency = (() => {
    if (isAcceptGoalFlow && pendingInvite?.recurringDepositSchedule) {
      return pendingInvite.recurringDepositSchedule;
    }

    return goal?.sips?.[0]?.recurringDepositSchedule ?? SipFrequency.Weekly;
  })();

  const initialBalance = convertCentsToDollar(goal?.currentAmountInCents);

  const recurringAmount =
    recurringDepositInCents != null
      ? convertCentsToDollar(recurringDepositInCents)
      : DEFAULT_RECURRING_AMOUNT;

  const props: ProjectedChartProviderProps = {
    accountType,
    initialBalance,
    projectedAge,
    contributeAfter18,
    multiplier,
    recurringAmount,
    frequency,
    flow,
    pendingInvite,
  };

  return (
    <ProjectedChartProvider {...props}>
      <GoalDepositRouteComponent />
    </ProjectedChartProvider>
  );
};

export const GoalDepositRouteComponent = () => {
  const {
    goalProps: { flow, accountType: accountTypeFromGoalFlow },
  } = useGoalFlow();

  const {
    accountType: accountTypeFromChart,
    recurringAmount: recurringAmountValue,
    recurringDepositType,
    isDirty,
  } = useProjectedChart();

  const { sip, goal } = useGoalsContext();

  const accountType = accountTypeFromChart ?? accountTypeFromGoalFlow ?? goal?.accounts?.[0]?.type;

  const { handleSubmit, loading, shouldPauseRecurringDeposits, shouldUnpauseRecurringDeposits } =
    useGoalDepositScreenFlow();

  const minDepositAmount = getMinDepositAmount(accountType);

  const disabled =
    (flow !== Flow.EditGoal &&
      (recurringAmountValue === 0 || recurringAmountValue < minDepositAmount)) ||
    (flow === Flow.EditGoal &&
      recurringAmountValue > 0 &&
      recurringAmountValue < minDepositAmount) ||
    (flow === Flow.EditGoal &&
      recurringAmountValue === 0 &&
      (sip?.status === SipStatus.Paused || !sip)) ||
    (flow === Flow.EditGoal && !isDirty && sip?.status !== SipStatus.Paused);

  const primaryCTALabel = (() => {
    if (shouldPauseRecurringDeposits) {
      return i18n.t(Translations.GoalDepositRoutePauseCTA);
    }
    if (shouldUnpauseRecurringDeposits) {
      return i18n.t(Translations.GoalDepositRouteUnpauseCTA);
    }
    return i18n.t(Translations.GoalDepositRoutePrimaryCTA);
  })();

  const sipStartInfoKey = getSipStartInfoKey(recurringDepositType);

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.GoalDepositRouteTitle),
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: primaryCTALabel,
        onClick: handleSubmit,
        loading,
        disabled,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <Spacing size={SpacingSizes.M} />
      <ProjectedChart header={<ChartHeader />} footer={<ChartFooter />} />
      <Spacing size={SpacingSizes.M} />

      <DepositAdjuster />
      <Spacing size={SpacingSizes.M} />

      <SharedContribution />

      <TargetYearAdjuster />
      <Spacing size={SpacingSizes.M} />

      <ProjectionCalculationModal />

      {sipStartInfoKey ? (
        <>
          <Caption style={styles.sipStartInfo}>{i18n.t(sipStartInfoKey)}</Caption>
          <Spacing size={SpacingSizes.S} />
        </>
      ) : null}
    </OnboardingLayout>
  );
};
